import { createContext, useContext, useMemo, type ComponentType, type PropsWithChildren } from 'react';
import { useMarketplaceConfig } from '../queries/marketplace';
import { MARKETPLACE_AUTH_MODEL } from '../types/enums';
import { type AuthFormsConfig } from '../config/skinRegistry/authForms';
import { type RegisterFormProps } from '../components/organisms/RegisterForm';

type ResolvedAuthForms = {
  LoginForm: ComponentType | undefined;
  RegisterForm: ComponentType<RegisterFormProps>;
  ForgotPasswordForm: ComponentType;
  CreateBOUsersForm: ComponentType;
};

type AuthFormsProviderProps = PropsWithChildren<{
  config: AuthFormsConfig;
}>;

const AuthFormsContext = createContext<ResolvedAuthForms | null>(null);

export const useAuthFormsContext = () => {
  const context = useContext(AuthFormsContext);

  if (!context) throw new Error('useAuthFormsContext must be used within AuthFormsProvider');

  return context;
};

export const AuthFormsProvider = ({ children, config }: AuthFormsProviderProps) => {
  const { data } = useMarketplaceConfig();

  const value = useMemo<ResolvedAuthForms>(() => {
    const model = data?.marketplaceConfig.model ?? MARKETPLACE_AUTH_MODEL.USER_REGISTRATION;

    return {
      LoginForm: config.loginFormsByModel[model],
      RegisterForm: config.RegisterForm,
      ForgotPasswordForm: config.ForgotPasswordForm,
      CreateBOUsersForm: config.CreateBOUsersForm,
    };
  }, [data?.marketplaceConfig.model, config]);

  return <AuthFormsContext.Provider value={value}>{children}</AuthFormsContext.Provider>;
};
